import { defineStore } from 'pinia';
import {
  getUserId,
  getWalletAddress,
  hasAuthSession,
  isAuthSessionEphemeral,
} from '@/utils/auth-token';
import { getAuthGeneration } from '@/utils/auth-teardown';
import { sessionExpectsCustodialWallet } from '@/utils/custodial-session-gate';
import { probePrivySignSession, recoverPrivySignSession } from '@/utils/privy';

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 回前台 / focus 冷却，避免反复 syncWithToken */
const FOCUS_COOLDOWN_MS = 60_000;

/** @type {Promise<{ ok: boolean, code?: string }>|null} */
let inflightEnsure = null;
let inflightGeneration = -1;
let inflightUserId = '';
let inflightAddress = '';
let ensureRunId = 0;

function currentAuthSession() {
  return {
    generation: getAuthGeneration(),
    userId: String(getUserId() ?? '').trim(),
    address: String(getWalletAddress() ?? '')
      .trim()
      .toLowerCase(),
  };
}

function isEnsureRunCurrent(run) {
  const current = currentAuthSession();
  return (
    run.runId === ensureRunId &&
    run.generation === current.generation &&
    run.userId === current.userId &&
    (!run.address || run.address === current.address) &&
    hasAuthSession()
  );
}

/**
 * Privy 签名会话就绪态（与 custodialSession 绑定门禁分离）。
 * 触发：登录后 / 刷新冷启动；回前台仅 probe，needsRepair 才 recover。
 */
export const usePrivySignSessionStore = defineStore('privySignSession', {
  state: () => ({
    /** @type {'idle'|'checking'|'recovering'|'ready'|'error'} */
    status: 'idle',
    /** @type {string} */
    errorCode: '',
    attempt: 0,
    lastCheckedAt: 0,
    readyUserId: '',
    readyAddress: '',
    /** 仅 memory 落盘时为 true（与平台 ephemeral 同口径参考） */
    ephemeral: false,
  }),

  getters: {
    isSignReady: (s) => s.status === 'ready',
    isChecking: (s) => s.status === 'checking' || s.status === 'recovering',
    isError: (s) => s.status === 'error',
    needsRepair() {
      if (!hasAuthSession()) return false;
      if (!sessionExpectsCustodialWallet()) return false;
      if (this.status === 'error' || this.status === 'idle') return true;
      const current = currentAuthSession();
      return (
        this.status !== 'ready' ||
        this.readyUserId !== current.userId ||
        this.readyAddress !== current.address
      );
    },
  },

  actions: {
    reset() {
      ensureRunId += 1;
      inflightEnsure = null;
      inflightGeneration = -1;
      inflightUserId = '';
      inflightAddress = '';
      this.status = 'idle';
      this.errorCode = '';
      this.attempt = 0;
      this.lastCheckedAt = 0;
      this.readyUserId = '';
      this.readyAddress = '';
      this.ephemeral = false;
    },

    /**
     * 登录后 / 刷新：需要时 ensure（含静默 recover）。
     * @param {{ maxAttempts?: number, force?: boolean }} [opts]
     */
    ensureIfNeeded(opts = {}) {
      if (!hasAuthSession()) {
        this.reset();
        return Promise.resolve({ ok: false, code: 'NO_SESSION' });
      }
      if (!sessionExpectsCustodialWallet()) {
        const current = currentAuthSession();
        this.status = 'ready';
        this.errorCode = '';
        this.lastCheckedAt = Date.now();
        this.readyUserId = current.userId;
        this.readyAddress = current.address;
        return Promise.resolve({ ok: true, skipped: true });
      }
      if (!opts.force && this.status === 'ready' && !this.needsRepair) {
        return Promise.resolve({ ok: true });
      }
      return this.ensure({
        force: Boolean(opts.force),
        maxAttempts: opts.maxAttempts,
      });
    },

    /**
     * 回前台：冷却内且已 ready 则跳过；否则 ensureIfNeeded。
     */
    ensureOnForeground() {
      if (!hasAuthSession() || !sessionExpectsCustodialWallet()) {
        return this.ensureIfNeeded();
      }
      const elapsed = Date.now() - Number(this.lastCheckedAt || 0);
      if (this.status === 'ready' && !this.needsRepair && elapsed < FOCUS_COOLDOWN_MS) {
        return Promise.resolve({ ok: true, skipped: true });
      }
      if (this.status === 'ready') {
        // 轻量再 probe；挂了再 recover
        return this.ensure({ force: false, maxAttempts: 2, probeFirst: true });
      }
      return this.ensureIfNeeded({ maxAttempts: 2 });
    },

    /**
     * @param {{ force?: boolean, maxAttempts?: number, probeFirst?: boolean }} [opts]
     * @returns {Promise<{ ok: boolean, code?: string, skipped?: boolean }>}
     */
    ensure(opts = {}) {
      const force = Boolean(opts.force);
      const maxAttempts = Math.max(1, Number(opts.maxAttempts) || 3);
      const probeFirst = opts.probeFirst !== false;

      if (!hasAuthSession()) {
        this.reset();
        return Promise.resolve({ ok: false, code: 'NO_SESSION' });
      }
      if (!sessionExpectsCustodialWallet()) {
        const current = currentAuthSession();
        this.status = 'ready';
        this.errorCode = '';
        this.lastCheckedAt = Date.now();
        this.readyUserId = current.userId;
        this.readyAddress = current.address;
        return Promise.resolve({ ok: true, skipped: true });
      }

      if (!force && this.status === 'ready' && !this.needsRepair) {
        return Promise.resolve({ ok: true });
      }

      const session = currentAuthSession();
      if (
        inflightEnsure &&
        inflightGeneration === session.generation &&
        inflightUserId === session.userId &&
        inflightAddress === session.address
      ) {
        return inflightEnsure;
      }
      if (inflightEnsure) ensureRunId += 1;

      const run = { ...session, runId: ++ensureRunId };
      inflightGeneration = session.generation;
      inflightUserId = session.userId;
      inflightAddress = session.address;
      const task = this._runEnsure(maxAttempts, probeFirst, run).finally(() => {
        if (inflightEnsure !== task) return;
        inflightEnsure = null;
        inflightGeneration = -1;
        inflightUserId = '';
        inflightAddress = '';
      });
      inflightEnsure = task;
      return task;
    },

    /** 手动重试 */
    retry() {
      return this.ensure({ force: true, maxAttempts: 3, probeFirst: false });
    },

    /**
     * @param {number} maxAttempts
     * @param {boolean} probeFirst
     */
    async _runEnsure(maxAttempts, probeFirst, run) {
      this.ephemeral = isAuthSessionEphemeral();
      this.errorCode = '';
      this.attempt = 0;

      let lastCode = 'UNKNOWN';

      for (let i = 0; i < maxAttempts; i += 1) {
        this.attempt = i + 1;
        if (i > 0) {
          await delay(1000 * 2 ** (i - 1));
        }
        if (!isEnsureRunCurrent(run)) return { ok: false, code: 'SESSION_CHANGED' };

        if (probeFirst || i === 0) {
          this.status = 'checking';
          const probe = await probePrivySignSession();
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'SESSION_CHANGED' };
          this.lastCheckedAt = Date.now();
          if (probe.ok) {
            const current = currentAuthSession();
            this.status = 'ready';
            this.errorCode = '';
            this.readyUserId = current.userId;
            this.readyAddress = current.address;
            this.ephemeral = isAuthSessionEphemeral();
            return { ok: true, skipped: probe.skipped };
          }
          lastCode = probe.code || lastCode;
        }

        this.status = 'recovering';
        const recovered = await recoverPrivySignSession();
        if (!isEnsureRunCurrent(run)) return { ok: false, code: 'SESSION_CHANGED' };
        this.lastCheckedAt = Date.now();
        if (recovered.ok) {
          const current = currentAuthSession();
          this.status = 'ready';
          this.errorCode = '';
          this.readyUserId = current.userId;
          this.readyAddress = current.address;
          this.ephemeral = isAuthSessionEphemeral();
          return { ok: true };
        }
        lastCode = recovered.code || lastCode;
      }

      if (!isEnsureRunCurrent(run)) return { ok: false, code: 'SESSION_CHANGED' };
      this.status = 'error';
      this.errorCode = lastCode;
      return { ok: false, code: lastCode };
    },
  },
});
